// Customer-facing overview of each program's training progression (simplified from Rep Lab's internal station guides).
const CONTENT = {
  hitting: {
    headline: "Prepare. Swing. Compete. Adjust.",
    intro: "Our hitting program develops better preparation, more consistent contact, and the ability to make adjustments when the game gets difficult.",
    stages: [["Movement Preparation", "Prepare the body to move efficiently and consistently."], ["Swing Development", "Build repeatable movements, effective swing direction, and better contact."],
      ["Controlled Hitting", "Apply those movements against a moving baseball with an emphasis on timing and quality contact."], ["Competitive Reps", "Take scored swings in a competitive environment where every result counts."],
      ["Feedback & Adjustments", "Review contact quality, understand tendencies, and identify what to improve."]],
    measureTitle: "How We Measure Progress",
    measure: "Our hitting scoring system evaluates contact outcomes, from strikeouts and weak contact to hard ground balls, line drives, and extra-base hits. Athletes can review their recorded performance over time and use the feedback to guide future adjustments.",
  },
  pitching: {
    headline: "Prepare. Develop. Throw. Compete.",
    intro: "Our pitching program develops stronger movement patterns, more consistent deliveries, and better command through structured preparation and competitive throwing.",
    stages: [["Movement & Arm Care", "Build a repeatable preparation routine emphasizing mobility, stability, and throwing readiness."], ["Mechanical Development", "Develop controlled movement patterns and improve delivery consistency."],
      ["Throwing Progression", "Apply mechanical improvements through a structured throwing sequence."], ["Command Challenge", "Compete in a scored pitching challenge focused on executing pitches and locating the baseball."],
      ["Review & Adjustment", "Evaluate command results and identify the next area of development."]],
    measureTitle: "How We Measure Progress",
    measure: "Our pitching scoring system records strikes to intended locations, strikes outside intended locations, and balls. Athletes receive objective command feedback and can evaluate performance across training sessions.",
  },
  strength: {
    headline: "Move Better. Build Strength. Develop Control.",
    intro: "Our age-appropriate strength program focuses on foundational movement quality, strength, stability, and athletic control.",
    stages: [["Movement Preparation", "Prepare the body with mobility, coordination, and controlled movement."], ["Upper-Body Development", "Develop pushing, pulling, and shoulder stability."],
      ["Lower-Body Development", "Build strength through squat and lunge movement patterns."], ["Stability & Body Control", "Develop balance, trunk stability, and the ability to control different positions."],
      ["Movement Quality", "Reinforce controlled movement, proper execution, and consistent effort."]],
    measureTitle: "Development Focus",
    measure: "Our goal is to help young athletes become stronger, more coordinated, and more confident in their movement.",
  },
};

export const hasStages = (key) => !!CONTENT[key];

export function StagesIntro({ k }) {
  const c = CONTENT[k];
  if (!c) return null;
  return (
    <div data-testid="program-stages" className="mt-8">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#60a5fa]">{c.headline}</p>
      <p className="mt-2 text-base font-medium text-white/70 max-w-2xl">{c.intro}</p>
      <p className="mt-6 text-xs font-bold uppercase tracking-wider text-white/40">What to expect<span className="sm:hidden"> · swipe</span></p>
      <ol className="mt-3 flex sm:grid sm:grid-cols-5 gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-5 px-5 sm:mx-0 sm:px-0">
        {c.stages.map(([t, d], i) => (
          <li key={t} data-testid={`stage-${i + 1}`} className="min-w-[68%] sm:min-w-0 snap-start rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <span className="text-sm font-extrabold text-[#60a5fa]">{String(i + 1).padStart(2, "0")}</span>
            <span className="mt-1 block text-sm font-extrabold uppercase tracking-tight leading-tight">{t}</span>
            <span className="mt-1 block text-xs font-medium text-white/55 leading-snug">{d}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function StagesMeasure({ k }) {
  const c = CONTENT[k];
  if (!c) return null;
  return (
    <div data-testid="program-measure" className="mt-14 border-t border-white/10 pt-8">
      <p className="text-xs font-bold uppercase tracking-wider text-white/40">{c.measureTitle}</p>
      <p className="mt-2 text-base font-medium text-white/70 max-w-2xl">{c.measure}</p>
    </div>
  );
}
